
'use client'

import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/stores/page-title-store'
import {
  DEPARTMENTS,
  ContractType,
  PerformanceLevel,
  UI_LABELS
} from '@/config/constants'
import {
  // Keep these for compatibility during transition
  caseInsensitiveEquals,
  caseInsensitiveIncludes
} from '@/lib/real-data-service'
import {
  PERFORMANCE_DISTRIBUTION,
  PERFORMANCE_MAPPING,
  mapEmployeePerformanceToCategory,
  getEmployeesByPerformanceCategory
} from '@/lib/services/performance.service'
// NEW: Import workforce API with scoped endpoints
import {
  listEmployees,
  listEmployeesScoped,
  getEmployeeStats,
  getMyAllReports
} from '@/api/workforce'
import {
  Users,
  Search,
  Filter,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Building,
  Clock,
  FileText,
  Edit,
  MoreVertical,
  User,
  Shield,
  Award,
  AlertTriangle
} from 'lucide-react'

// Helper function to get count based on PERFORMANCE_DISTRIBUTION
function getPerformanceCount(performanceCategory: string): number {
  // Map from performance category to original Excel categories
  const reverseMapping = Object.fromEntries(
    Object.entries(PERFORMANCE_MAPPING).map(([key, value]) => [value, key])
  );
  
  const excelCategory = reverseMapping[performanceCategory];
  return excelCategory && excelCategory in PERFORMANCE_DISTRIBUTION 
    ? PERFORMANCE_DISTRIBUTION[excelCategory as keyof typeof PERFORMANCE_DISTRIBUTION]?.count || 0 
    : 0;
}

function EmployeeCard({ employee }: { employee: any }) {
  // NEW: Extract data from workforce API structure with safe access
  const employeeName = employee.personalInfo?.name || employee.name || 'Nome não informado'
  const employeeRole = employee.employment?.role?.name || employee.position || employee.role || 'Cargo não informado'
  const employeeDepartment = employee.employment?.department?.name || employee.department || 'Departamento não informado'
  const employeeContractType = employee.employment?.contractType || employee.contractType || 'CLT'
  const employeeEmail = employee.email || 'Email não informado'
  const employeeStartDate = employee.employment?.startDate || employee.admissionDate || employee.startDate
  const employeeId = employee.employeeId || employee.id || 'ID não informado'

  const getStatusColor = (contractType: string) => {
    switch (contractType) {
      case 'CLT':
        return 'status-active'
      case 'PJ':
        return 'status-pending'
      case 'SOCIO':
        return 'status-inactive'
      default:
        return 'status-inactive'
    }
  }

  const getStatusText = (contractType: string) => {
    switch (contractType) {
      case 'CLT':
        return 'CLT'
      case 'PJ':
        return 'PJ'
      case 'SOCIO':
        return 'Sócio'
      default:
        return 'Indefinido'
    }
  }

  return (
    <div className="card-interactive group">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center mr-4 shadow-monumenta-brand group-hover:shadow-monumenta-brand-lg transition-all duration-300">
            <span className="text-lg font-bold text-brand-dark font-display">
              {employeeName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-white mb-1 group-hover:text-brand-primary transition-colors duration-200">
              {employeeName}
            </h3>
            <p className="text-brand-gray text-sm font-body">{employeeRole}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`${getStatusColor(employeeContractType)} text-xs font-display`}>
            {getStatusText(employeeContractType)}
          </span>
          {/* <button className="btn-icon text-brand-gray hover:text-brand-primary">
            <MoreVertical size={16} />
          </button> */}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center text-sm text-gray-300">
          <Building className="text-brand-primary mr-2" size={16} />
          <span>{employeeDepartment}</span>
        </div>
        
        <div className="flex items-center text-sm text-gray-300">
          <Mail className="text-brand-primary mr-2" size={16} />
          <span>{employeeEmail}</span>
        </div>
        
        <div className="flex items-center text-sm text-gray-300">
          <User className="text-brand-primary mr-2" size={16} />
          <span>{employee.client || 'Cliente não informado'}</span>
        </div>
        
        <div className="flex items-center text-sm text-gray-300">
          <Calendar className="text-brand-primary mr-2" size={16} />
          <span>
            Desde {employeeStartDate ? new Date(employeeStartDate).toLocaleDateString('pt-BR') : 'Data não informada'}
          </span>
        </div>
      </div>

      {/* <div className="mt-6 pt-4 border-t border-gray-700/50">
        <div className="flex items-center justify-between">
          <div className="flex space-x-3">
            <button className="text-xs text-brand-primary hover:text-brand-accent-600 font-display font-medium transition-colors duration-200">
              Ver Perfil
            </button>
            <button className="text-xs text-brand-gray hover:text-white font-display font-medium transition-colors duration-200">
              Editar
            </button>
          </div>
        </div>
      </div> */}
    </div>
  )
}

function FilterSidebar({ 
  filters, 
  setFilters, 
  isOpen, 
  onClose 
}: {
  filters: any
  setFilters: (filters: any) => void
  isOpen: boolean
  onClose: () => void
}) {
  const departments = [UI_LABELS.ALL_FILTER, ...DEPARTMENTS]
  const contractTypes = [UI_LABELS.ALL_FILTER, ...Object.values(ContractType)]
  const performanceTypes = [UI_LABELS.ALL_FILTER, ...Object.values(PerformanceLevel)]

  return (
    <>
      {/* Mobile Filter Panel (Bottom Sheet Style) */}
      <div className={`
        fixed inset-x-0 bottom-0 z-50 bg-brand-dark-light border-t border-gray-700 transform transition-transform duration-300 ease-in-out sm:hidden
        ${isOpen ? 'translate-y-0' : 'translate-y-full'}
      `}>
        <div className="p-4 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-white">
              Filtros
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">
              ×
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="form-label">Departamento</label>
              <select
                value={filters.department}
                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                className="form-input w-full"
              >
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Tipo de Contrato</label>
              <select
                value={filters.contractType}
                onChange={(e) => setFilters({ ...filters, contractType: e.target.value })}
                className="form-input w-full"
              >
                {contractTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Performance</label>
              <select
                value={filters.performance}
                onChange={(e) => setFilters({ ...filters, performance: e.target.value })}
                className="form-input w-full"
              >
                {performanceTypes.map(performance => (
                  <option key={performance} value={performance}>{performance}</option>
                ))}
              </select>
            </div>

            <div className="flex space-x-3 pt-2">
              <button
                onClick={() => setFilters({ department: 'Todos', contractType: 'Todos', performance: 'Todos' })}
                className="btn-secondary flex-1"
              >
                Limpar
              </button>
              <button
                onClick={onClose}
                className="btn-primary flex-1"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Filter Sidebar */}
      <div className={`
        fixed inset-y-0 right-0 z-50 w-80 bg-brand-dark-light border-l border-gray-700 transform transition-transform duration-300 ease-in-out hidden sm:block
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-lg font-semibold text-white">
              Filtros
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              ×
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <label className="form-label">Departamento</label>
              <select
                value={filters.department}
                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                className="form-input w-full"
              >
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Tipo de Contrato</label>
              <select
                value={filters.contractType}
                onChange={(e) => setFilters({ ...filters, contractType: e.target.value })}
                className="form-input w-full"
              >
                {contractTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Performance</label>
              <select
                value={filters.performance}
                onChange={(e) => setFilters({ ...filters, performance: e.target.value })}
                className="form-input w-full"
              >
                {performanceTypes.map(performance => (
                  <option key={performance} value={performance}>{performance}</option>
                ))}
              </select>
            </div>

            <div className="pt-4 border-t border-gray-700">
              <button
                onClick={() => setFilters({ department: 'Todos', contractType: 'Todos', performance: 'Todos' })}
                className="btn-secondary w-full"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function EmployeesContent() {
  const { user } = useAuth()
  usePageTitle('Colaboradores')
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    department: 'Todos',
    contractType: 'Todos',
    performance: 'Todos'
  })
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [isLoading, setIsLoading] = useState(true)
  const [allEmployees, setAllEmployees] = useState<any[]>([])
  const [error, setError] = useState<string | null>(null)
  const [employeeStats, setEmployeeStats] = useState<any>(null)

  // Load employees data asynchronously
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // NEW: Use hierarchy-based API to get all reports (any depth)
        // Get all reports under the current user using hierarchy
        const result = await getMyAllReports({ limit: 1000 });

        setAllEmployees(result.items)
        
        // Load stats separately
        try {
          const statsResponse = await getEmployeeStats('default')
          setEmployeeStats(statsResponse)
        } catch (statsErr) {
          console.warn('⚠️ Could not load employee stats:', statsErr)
        }
      } catch (err) {
        console.error('Error loading employees:', err)
        setError('Erro ao carregar lista de colaboradores')
      } finally {
        setIsLoading(false)
      }
    }

    loadEmployees()
  }, [])

  // Filter employees based on search and filters (case-insensitive)
  // The hierarchy API returns all reports under the user (any depth)
  const filteredEmployees = useMemo(() => {
    if (!allEmployees.length) return []

    const filtered = allEmployees.filter(employee => {
    // NEW: Safe access to employee data from workforce API structure
    const employeeName = employee.personalInfo?.name || employee.name || ''
    const employeeEmail = employee.email || ''
    const employeeRole = employee.employment?.role?.name || employee.position || employee.role || ''
    const employeeDepartment = employee.employment?.department?.name || employee.department || ''
    const employeeContractType = employee.employment?.contractType || employee.contractType || ''

    const matchesSearch = caseInsensitiveIncludes(employeeName, searchTerm) ||
                         caseInsensitiveIncludes(employeeEmail, searchTerm) ||
                         caseInsensitiveIncludes(employeeRole, searchTerm) ||
                         caseInsensitiveIncludes(employeeDepartment, searchTerm) ||
                         caseInsensitiveIncludes(employee.client || '', searchTerm)

    const matchesDepartment = filters.department === 'Todos' || caseInsensitiveEquals(employeeDepartment, filters.department)
    const matchesContractType = filters.contractType === 'Todos' || caseInsensitiveEquals(employeeContractType, filters.contractType)
    const matchesPerformance = filters.performance === 'Todos' ||
                          caseInsensitiveEquals(mapEmployeePerformanceToCategory(employee.performance || ''), filters.performance)

      return matchesSearch && matchesDepartment && matchesContractType && matchesPerformance
    })

    // Sort by name alphabetically
    return filtered.sort((a, b) => {
      const nameA = (a.personalInfo?.name || a.name || '').toLowerCase()
      const nameB = (b.personalInfo?.name || b.name || '').toLowerCase()
      return nameA.localeCompare(nameB, 'pt-BR')
    })
  }, [allEmployees, searchTerm, filters])

  // NEW: Check if user exists - moved from early return to conditional rendering
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <p className="text-brand-gray-light font-body">Usuário não encontrado</p>
        </div>
      </div>
    )
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto"></div>
          <p className="text-brand-gray-light font-body">Carregando colaboradores...</p>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-brand-red/20 rounded-full flex items-center justify-center mx-auto">
            <AlertTriangle className="h-8 w-8 text-brand-red" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white mb-2">Erro ao Carregar Colaboradores</h2>
            <p className="text-brand-gray-light">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary mt-4"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
    <div className="space-y-8">
        {/* Hierarchy Indicator */}
        {/* <div className="mb-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                <div>
                  <h3 className="text-sm font-medium text-blue-900">
                    Lista Baseada em Hierarquia: <span className="font-semibold">Todos os Reportes</span>
                  </h3>
                  <p className="text-xs text-blue-700 mt-1">
                    Exibindo {allEmployees.length} colaboradores que reportam a você (direta ou indiretamente)
                  </p>
                </div>
              </div>
              <div className="text-xs text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                Hierarquia Ativa
              </div>
            </div>
          </div>
        </div> */}

        {/* Stats Cards */}
        <div className="dashboard-grid lg:grid-cols-4">
          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-brand-primary" size={20} />
            </div>
            <div className="text-2xl font-display font-bold text-brand-primary mb-1">
              {allEmployees.length}
            </div>
            <div className="text-xs text-gray-400 uppercase tracking-wider">
              Total Colaboradores
            </div>
          </div>
          
          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <Shield className="text-success" size={20} />
            </div>
            <div className="text-2xl font-display font-bold text-success mb-1">
              {allEmployees.filter((e: any) => (e.employment?.status || 'ACTIVE') === 'ACTIVE').length}
            </div>
            <div className="text-xs text-gray-400 uppercase tracking-wider">
              Colaboradores Ativos
            </div>
          </div>
          
          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-warning" size={20} />
            </div>
            <div className="text-2xl font-display font-bold text-warning mb-1">
              {employeeStats?.inactiveEmployees || allEmployees.filter((e: any) => e.contractType === 'CLT').length}
            </div>
            <div className="text-xs text-gray-400 uppercase tracking-wider">
              Inativos
            </div>
          </div>
          
          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <Award className="text-info" size={20} />
            </div>
            <div className="text-2xl font-display font-bold text-info mb-1">
              {employeeStats?.departmentBreakdown?.length || allEmployees.filter((e: any) => e.contractType === 'PJ').length}
            </div>
            <div className="text-xs text-gray-400 uppercase tracking-wider">
              Departamentos
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="flex-1 relative group">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-brand-primary transition-colors duration-200 pointer-events-none z-10" size={20} />
            <input
              type="text"
              placeholder="Buscar por nome, email ou cargo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input w-full pl-12 transition-all duration-200 hover:border-gray-600 focus:ring-brand-primary/20"
              style={{ paddingLeft: '3rem' }}
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setFiltersOpen(true)}
              className="btn-secondary flex items-center gap-2 flex-1 sm:flex-none justify-center group"
            >
              <Filter size={20} className="transition-transform duration-200 group-hover:rotate-12" />
              <span>Filtros</span>
            </button>
            
            <div className="flex border border-gray-700 rounded-monumenta overflow-hidden shadow-monumenta">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 text-sm font-display font-medium transition-all duration-300 ${
                  viewMode === 'grid' 
                    ? 'bg-brand-primary text-brand-dark shadow-monumenta-brand' 
                    : 'text-gray-400 hover:text-white hover:bg-brand-dark-lighter'
                }`}
              >
                <span className="hidden sm:inline">Grade</span>
                <span className="sm:hidden">•</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 text-sm font-display font-medium transition-all duration-300 ${
                  viewMode === 'list' 
                    ? 'bg-brand-primary text-brand-dark shadow-monumenta-brand' 
                    : 'text-gray-400 hover:text-white hover:bg-brand-dark-lighter'
                }`}
              >
                <span className="hidden sm:inline">Lista</span>
                <span className="sm:hidden">≡</span>
              </button>
            </div>
          </div>
        </div>

        {/* Results Info */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm space-y-1 sm:space-y-0 p-4 bg-brand-dark-light/50 rounded-monumenta border border-gray-800/50">
          <span className="text-brand-gray-light font-display font-medium">
            {filteredEmployees.length} colaborador{filteredEmployees.length !== 1 ? 'es' : ''} encontrado{filteredEmployees.length !== 1 ? 's' : ''}
          </span>
          <span className="text-xs sm:text-sm text-gray-400 font-body">
            Ordenado por nome
          </span>
        </div>

        {/* Employee Grid/List */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up">
            {filteredEmployees.map((employee: any) => (
              <EmployeeCard key={employee.employeeId || employee.id} employee={employee} />
            ))}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="card-hover hidden sm:block animate-fade-in-up">
              <div className="table-container">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th>Colaborador</th>
                      <th>Cargo</th>
                      <th>Departamento</th>
                      <th>Contrato</th>
                      <th>Performance</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {filteredEmployees.map((employee: any) => (
                      <tr key={employee.employeeId || employee.id}>
                        <td>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center mr-3">
                              <span className="text-xs font-bold text-brand-dark">
                                {(employee.personalInfo?.name || employee.name || '?').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="text-white font-medium">{employee.personalInfo?.name || employee.name || 'Nome não informado'}</div>
                              <div className="text-xs text-gray-400">{employee.email || 'Email não informado'}</div>
                            </div>
                          </div>
                        </td>
                        <td>{employee.employment?.role?.name || employee.position || employee.role || 'Cargo não informado'}</td>
                        <td>{employee.employment?.department?.name || employee.department || 'Departamento não informado'}</td>
                        <td>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            (employee.employment?.contractType || employee.contractType) === 'CLT' 
                              ? 'bg-info/20 text-info' 
                              : (employee.employment?.contractType || employee.contractType) === 'PJ'
                              ? 'bg-warning/20 text-warning'
                              : 'bg-gray-400/20 text-gray-400'
                          }`}>
                            {employee.employment?.contractType || employee.contractType || 'CLT'}
                          </span>
                        </td>
                        <td>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            mapEmployeePerformanceToCategory(employee.performance) === 'Alta Performance' ? 'bg-success/20 text-success' :
                            mapEmployeePerformanceToCategory(employee.performance) === 'Potencial Crescimento' ? 'bg-info/20 text-info' :
                            mapEmployeePerformanceToCategory(employee.performance) === 'Desenvolvimento' ? 'bg-warning/20 text-warning' :
                            'bg-gray-400/20 text-gray-400'
                          }`}>
                            {mapEmployeePerformanceToCategory(employee.performance) || 'Não avaliado'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile List View - Cards */}
            <div className="sm:hidden space-y-3">
              {filteredEmployees.map((employee: any) => (
                <div key={employee.employeeId || employee.id} className="card">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center mr-3">
                        <span className="text-sm font-bold text-brand-dark">
                          {(employee.personalInfo?.name || employee.name || '?').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-display text-base font-semibold text-white">
                          {employee.personalInfo?.name || employee.name || 'Nome não informado'}
                        </h3>
                        <p className="text-brand-gray text-sm">{employee.employment?.role?.name || employee.position || employee.role || 'Cargo não informado'}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      mapEmployeePerformanceToCategory(employee.performance) === 'Alta Performance' ? 'bg-success/20 text-success' :
                      mapEmployeePerformanceToCategory(employee.performance) === 'Potencial Crescimento' ? 'bg-info/20 text-info' :
                      mapEmployeePerformanceToCategory(employee.performance) === 'Desenvolvimento' ? 'bg-warning/20 text-warning' :
                      'bg-gray-400/20 text-gray-400'
                    }`}>
                      {mapEmployeePerformanceToCategory(employee.performance) || 'Não avaliado'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-400 block text-xs">Departamento</span>
                      <span className="text-white">{employee.employment?.department?.name || employee.department || 'Departamento não informado'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block text-xs">Contrato</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        (employee.employment?.contractType || employee.contractType) === 'CLT' 
                          ? 'bg-info/20 text-info' 
                          : (employee.employment?.contractType || employee.contractType) === 'PJ'
                          ? 'bg-warning/20 text-warning'
                          : 'bg-gray-400/20 text-gray-400'
                      }`}>
                        {employee.employment?.contractType || employee.contractType || 'CLT'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <div className="text-xs text-gray-400">
                      {employee.email || 'Email não informado'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Empty State */}
        {filteredEmployees.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              Nenhum colaborador encontrado
            </h3>
            <p className="text-gray-400 mb-4">
              Tente ajustar os filtros ou termos de busca
            </p>
            <button 
              onClick={() => {
                setSearchTerm('')
                setFilters({ department: 'Todos', contractType: 'Todos', performance: 'Todos' })
              }}
              className="btn-secondary"
            >
              Limpar Filtros
            </button>
          </div>
        )}
      </div>

      {/* Filter Sidebar */}
      <FilterSidebar 
        filters={filters}
        setFilters={setFilters}
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
      />

      {/* Overlay */}
      {filtersOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setFiltersOpen(false)}
        />
      )}
    </>
  )
}

export default function EmployeesPage() {
  return (
    <EmployeesContent />
  )
} 